import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ClipboardList, Plus, Save, AlertCircle, CheckCircle, Activity, Heart, Brain, Users, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import BodyMapSelector from './BodyMapSelector';
import MMGSection from './MMGSection';
import DictableTextarea from './DictableTextarea';
import ConfirmDialog from './ConfirmDialog';
import DatePickerField from '@/components/DatePickerField';

// === CONSTANTS ===
const PATOLOGIE = [
  'Diabete Mellito', 'BPCO', 'Ipertensione Arteriosa', 'Diagnosi Psichiatrica',
  'IMA/Cardiopatia Ischemica', 'Ictus/TIA', 'Neoplasie', 'Insufficienza Renale Cronica',
  'Cirrosi Epatica', 'Scompenso Cardiaco', 'Fibrillazione Atriale', 'Demenza',
  'Parkinson', 'Osteoporosi', 'Artrite/Artrosi', 'Epilessia'
];

const BISOGNI = [
  'Ridotta mobilita', 'O2 terapia', 'Alterazione ciclo sonno-veglia',
  'Ulcere cutanee/Lesioni da pressione', 'Incontinenza urinaria', 'Incontinenza fecale',
  'Disfagia', 'Nutrizione enterale (SNG/PEG)', 'Catetere vescicale',
  'Tracheostomia', 'Stomia', 'Diabete insulino-trattato',
  'Dolore cronico', 'Stato confusionale/Agitazione', 'Rischio caduta elevato',
  'Isolamento sociale', 'Deficit visivo', 'Deficit uditivo'
];

const ATTIVITA_INFERMIERISTICHE = [
  'Somministrazione terapia orale', 'Somministrazione terapia iniettiva',
  'Medicazioni lesioni', 'Gestione CVC/accessi venosi', 'Gestione catetere vescicale',
  'Gestione tracheostomia', 'Gestione stomia', 'Monitoraggio parametri vitali',
  'Prelievi ematici', 'Gestione O2 terapia', 'Valutazione dolore (NRS/VAS)',
  'Educazione sanitaria', 'Gestione nutrizione enterale'
];

const ATTIVITA_FISIO = [
  'Mobilizzazione passiva', 'Mobilizzazione attiva assistita', 'Rieducazione alla deambulazione',
  'Esercizi di equilibrio', 'FKT respiratoria', 'Verticalizzazione',
  'Elettroterapia', 'Terapia manuale', 'Prevenzione cadute'
];

const ATTIVITA_OSS = [
  'Igiene personale completa', 'Igiene parziale', 'Bagno assistito',
  'Vestizione/Svestizione', 'Aiuto alimentazione', 'Idratazione',
  'Mobilizzazione/Posizionamento', 'Accompagnamento deambulazione',
  'Cambio pannolone', 'Rifacimento letto', 'Sorveglianza notturna'
];

const ATTIVITA_TERAPISTA = [
  'Stimolazione cognitiva', 'Laboratorio creativi', 'Attivita ludico-ricreative',
  'Musicoterapia', 'Terapia occupazionale', 'Attivita di socializzazione',
  'Orientamento realta', 'Stimolazione sensoriale'
];

// === BARTHEL SCALE ===
const BARTHEL_ITEMS = [
  { nome: 'Alimentazione', opzioni: [{ v: 0, l: 'Dipendente' }, { v: 5, l: 'Necessita aiuto' }, { v: 10, l: 'Indipendente' }] },
  { nome: 'Bagno/Doccia', opzioni: [{ v: 0, l: 'Dipendente' }, { v: 5, l: 'Indipendente' }] },
  { nome: 'Igiene personale', opzioni: [{ v: 0, l: 'Necessita aiuto' }, { v: 5, l: 'Indipendente' }] },
  { nome: 'Vestirsi', opzioni: [{ v: 0, l: 'Dipendente' }, { v: 5, l: 'Necessita aiuto' }, { v: 10, l: 'Indipendente' }] },
  { nome: 'Continenza intestinale', opzioni: [{ v: 0, l: 'Incontinente' }, { v: 5, l: 'Occasionale' }, { v: 10, l: 'Continente' }] },
  { nome: 'Continenza urinaria', opzioni: [{ v: 0, l: 'Incontinente' }, { v: 5, l: 'Occasionale' }, { v: 10, l: 'Continente' }] },
  { nome: 'Uso WC', opzioni: [{ v: 0, l: 'Dipendente' }, { v: 5, l: 'Necessita aiuto' }, { v: 10, l: 'Indipendente' }] },
  { nome: 'Trasferimenti letto-sedia', opzioni: [{ v: 0, l: 'Non eseguibile' }, { v: 5, l: 'Aiuto maggiore' }, { v: 10, l: 'Aiuto minore' }, { v: 15, l: 'Indipendente' }] },
  { nome: 'Deambulazione', opzioni: [{ v: 0, l: 'Non deambula' }, { v: 5, l: 'Carrozzina indip.' }, { v: 10, l: 'Con aiuto' }, { v: 15, l: 'Indipendente' }] },
  { nome: 'Scale', opzioni: [{ v: 0, l: 'Non eseguibile' }, { v: 5, l: 'Necessita aiuto' }, { v: 10, l: 'Indipendente' }] },
];

// === BRADEN SCALE ===
const BRADEN_ITEMS = [
  { nome: 'Percezione sensoriale', opzioni: [{ v: 1, l: 'Completamente limitata' }, { v: 2, l: 'Molto limitata' }, { v: 3, l: 'Lievemente limitata' }, { v: 4, l: 'Nessuna limitazione' }] },
  { nome: 'Umidita', opzioni: [{ v: 1, l: 'Costantemente umida' }, { v: 2, l: 'Spesso umida' }, { v: 3, l: 'Occasionalmente umida' }, { v: 4, l: 'Raramente umida' }] },
  { nome: 'Attivita', opzioni: [{ v: 1, l: 'Allettato' }, { v: 2, l: 'In poltrona' }, { v: 3, l: 'Cammina occasionalmente' }, { v: 4, l: 'Cammina frequentemente' }] },
  { nome: 'Mobilita', opzioni: [{ v: 1, l: 'Completamente immobile' }, { v: 2, l: 'Molto limitata' }, { v: 3, l: 'Lievemente limitata' }, { v: 4, l: 'Nessuna limitazione' }] },
  { nome: 'Nutrizione', opzioni: [{ v: 1, l: 'Molto scarsa' }, { v: 2, l: 'Probabilmente inadeguata' }, { v: 3, l: 'Adeguata' }, { v: 4, l: 'Eccellente' }] },
  { nome: 'Frizione e scivolamento', opzioni: [{ v: 1, l: 'Problema' }, { v: 2, l: 'Problema potenziale' }, { v: 3, l: 'Nessun problema apparente' }] },
];

// === CONLEY SCALE ===
const CONLEY_ITEMS = [
  { nome: 'Precedenti cadute (ultimi 3 mesi)', opzioni: [{ v: 0, l: 'No' }, { v: 2, l: 'Si' }] },
  { nome: 'Vertigini/capogiri', opzioni: [{ v: 0, l: 'No' }, { v: 1, l: 'Si' }] },
  { nome: 'Perdita urine durante trasferimento', opzioni: [{ v: 0, l: 'No' }, { v: 1, l: 'Si' }] },
  { nome: 'Agitazione/Deterioramento cognitivo', opzioni: [{ v: 0, l: 'No' }, { v: 2, l: 'Si' }] },
  { nome: 'Deficit nella deambulazione', opzioni: [{ v: 0, l: 'No' }, { v: 2, l: 'Si' }] },
  { nome: 'Riduzione della vista', opzioni: [{ v: 0, l: 'No' }, { v: 1, l: 'Si' }] },
];

// === CDRS - Clinical Dementia Rating Scale (Estesa) ===
// Ref: Morris, J. C. (1997). International Psychogeriatrics, 9: 173-176
const CDRS_ITEMS = [
  { nome: 'Memoria', primaria: true, opzioni: [
    { v: 0, l: 'Nessuna perdita di memoria o smemoratezza occasionale ed irrilevante' },
    { v: 0.5, l: 'Lieve smemoratezza permanente; parziale rievocazione di eventi' },
    { v: 1, l: 'Perdita di memoria moderata e piu rilevante per eventi recenti, con interferenza nelle attivita quotidiane' },
    { v: 2, l: 'Perdita di memoria severa: il materiale nuovo viene perso rapidamente' },
    { v: 3, l: 'Perdita di memoria grave: rimangono solo alcuni frammenti' },
  ]},
  { nome: 'Orientamento', opzioni: [
    { v: 0, l: 'Perfettamente orientato' },
    { v: 0.5, l: 'Ben orientato, eccetto lieve difficolta nell\'orientamento temporale' },
    { v: 1, l: 'Moderato deficit nell\'orientamento temporale; orientato nello spazio durante la visita, ma altrove puo essere disorientato' },
    { v: 2, l: 'Severo disorientamento temporale, spesso anche spaziale' },
    { v: 3, l: 'Orientamento solo personale' },
  ]},
  { nome: 'Giudizio e soluzione di problemi', opzioni: [
    { v: 0, l: 'Risolve bene i problemi quotidiani e gestisce bene sia gli affari che le finanze; giudizio adeguato rispetto al passato' },
    { v: 0.5, l: 'Lieve compromissione nella soluzione di problemi, analogie e differenze (prove di ragionamento)' },
    { v: 1, l: 'Difficolta moderata nella gestione dei problemi, analogie e differenze; giudizio sociale di solito conservato' },
    { v: 2, l: 'Difficolta severa nell\'esecuzione di problemi, analogie e differenze; giudizio sociale compromesso' },
    { v: 3, l: 'Incapace di dare giudizi o di risolvere problemi' },
  ]},
  { nome: 'Vita di comunita', opzioni: [
    { v: 0, l: 'Usuali livelli di autonomia funzionale nel lavoro, negli acquisti, nelle attivita di volontariato e nelle relazioni sociali' },
    { v: 0.5, l: 'Lieve compromissione nel lavoro, negli acquisti, nelle attivita di volontariato e nelle relazioni sociali' },
    { v: 1, l: 'Incapace di compiere indipendentemente queste attivita, anche se puo ancora essere coinvolto in alcune; appare normale ad un esame casuale' },
    { v: 2, l: 'Nessuna pretesa di attivita indipendente fuori casa; in grado di essere portato fuori casa' },
    { v: 3, l: 'Nessuna attivita indipendente fuori casa; non in grado di uscire fuori casa' },
  ]},
  { nome: 'Casa e hobbies', opzioni: [
    { v: 0, l: 'Vita domestica, hobbies e interessi intellettuali ben conservati' },
    { v: 0.5, l: 'Vita domestica, hobbies e interessi intellettuali lievemente compromessi' },
    { v: 1, l: 'Lieve ma sensibile compromissione della vita domestica; abbandono dei lavori domestici piu difficili e degli hobbies ed interessi piu complicati' },
    { v: 2, l: 'Conservati solo lavori domestici semplici; interessi ridotti e non sostenuti' },
    { v: 3, l: 'Nessuna funzione domestica conservata' },
  ]},
  { nome: 'Cura personale', nota: 'La CDR non prevede il livello 0.5 per questo dominio', opzioni: [
    { v: 0, l: 'Interamente capace di curarsi della propria persona' },
    { v: 1, l: 'Richiede sollecitazione per la normale cura personale' },
    { v: 2, l: 'Richiede assistenza per abbigliamento, igiene e cura della persona' },
    { v: 3, l: 'Richiede molta assistenza per la cura personale; spesso incontinenza urinaria' },
  ]},
];

// CDR 4 e CDR 5 - stadi avanzati (valutazione globale, non per dominio)
const CDR_STADI_AVANZATI = [
  { valore: 4, nome: 'CDR 4 - Demenza molto grave', descrizione: 'Severo deficit del linguaggio o della comprensione, problemi nel riconoscere i familiari, incapacita a deambulare in modo autonomo, problemi ad alimentarsi da solo, nel controllare la funzione intestinale o vescicale' },
  { valore: 5, nome: 'CDR 5 - Demenza terminale', descrizione: 'Il paziente richiede assistenza totale perche completamente incapace di comunicare, in stato vegetativo, allettato, incontinente' },
];

// Calcolo CDR Globale secondo algoritmo ufficiale
// La memoria e la categoria primaria; le altre cinque sono secondarie
function calcolaCDRGlobale(cdrsValues) {
  const memoria = cdrsValues[0];
  if (memoria == null) return null;
  const secondarie = [1, 2, 3, 4, 5].map(i => cdrsValues[i]).filter(v => v != null);
  if (secondarie.length < 3) return null;

  const uguali = secondarie.filter(v => v === memoria).length;
  // Se almeno 3 secondarie = memoria, CDR = memoria
  if (uguali >= 3) return memoria;

  const superiori = secondarie.filter(v => v > memoria).length;
  const inferiori = secondarie.filter(v => v < memoria).length;

  // Se 3+ secondarie sono superiori o inferiori, CDR = punteggio della maggioranza delle secondarie
  if (superiori >= 3 || inferiori >= 3) {
    const freq = new Map();
    secondarie.forEach(v => freq.set(v, (freq.get(v) || 0) + 1));
    let best = memoria;
    let bestCount = -1;
    freq.forEach((count, v) => {
      const piuFrequente = count > bestCount;
      const pariMerito = count === bestCount && Math.abs(v - memoria) < Math.abs(best - memoria);
      if (piuFrequente || pariMerito) { best = v; bestCount = count; }
    });
    return best;
  }

  // Due superiori e due inferiori (o nessuna maggioranza): CDR = memoria
  return memoria;
}

// === OBIETTIVI SUGGERITI PER BISOGNO ===
const OBIETTIVI_PER_BISOGNO = {
  'Ridotta mobilita': [
    'Mantenere il livello di mobilita residua',
    'Prevenire complicanze da immobilizzazione',
    'Favorire la mobilizzazione attiva/passiva quotidiana',
  ],
  'O2 terapia': [
    'Mantenere saturazione O2 > 92%',
    'Garantire corretta gestione dei presidi per ossigenoterapia',
  ],
  'Alterazione ciclo sonno-veglia': [
    'Favorire il ripristino del ritmo sonno-veglia',
    'Ridurre il numero di risvegli notturni',
  ],
  'Ulcere cutanee/Lesioni da pressione': [
    'Promuovere la guarigione delle lesioni cutanee',
    'Prevenire la comparsa di nuove lesioni da pressione',
    'Attuare protocollo di cambio posturale',
  ],
  'Incontinenza urinaria': [
    'Gestire l\'incontinenza preservando la dignita',
    'Prevenire lesioni cutanee da macerazione',
    'Monitorare la diuresi giornaliera',
  ],
  'Incontinenza fecale': [
    'Gestire l\'incontinenza fecale preservando comfort e dignita',
    'Monitorare l\'alvo e prevenire complicanze cutanee',
  ],
  'Disfagia': [
    'Prevenire episodi di aspirazione/ab ingestis',
    'Garantire adeguato apporto nutrizionale con consistenze modificate',
  ],
  'Nutrizione enterale (SNG/PEG)': [
    'Mantenere adeguato stato nutrizionale via enterale',
    'Prevenire complicanze legate al presidio (SNG/PEG)',
  ],
  'Catetere vescicale': [
    'Prevenire infezioni urinarie catetere-correlate',
    'Gestione corretta del catetere e monitoraggio diuresi',
  ],
  'Tracheostomia': [
    'Mantenere pervie le vie aeree',
    'Prevenire infezioni tracheostomiche',
  ],
  'Stomia': [
    'Gestione corretta della stomia e del presidio',
    'Prevenire complicanze peristomali',
  ],
  'Diabete insulino-trattato': [
    'Mantenere glicemia entro range terapeutico',
    'Prevenire episodi ipoglicemici e iperglicemici',
  ],
  'Dolore cronico': [
    'Controllo del dolore (NRS/VAS < 3)',
    'Migliorare la qualita di vita riducendo il dolore',
  ],
  'Stato confusionale/Agitazione': [
    'Ridurre episodi di agitazione psicomotoria',
    'Garantire sicurezza dell\'ospite e degli altri',
    'Favorire un ambiente calmo e orientante',
  ],
  'Rischio caduta elevato': [
    'Prevenire cadute accidentali',
    'Adottare misure di sicurezza ambientale',
    'Monitorare fattori di rischio (farmaci, ipotensione)',
  ],
  'Isolamento sociale': [
    'Favorire la partecipazione ad attivita di socializzazione',
    'Promuovere le relazioni con familiari e altri ospiti',
  ],
  'Deficit visivo': [
    'Garantire sicurezza ambientale adeguata al deficit',
    'Verificare adeguatezza ausili visivi',
  ],
  'Deficit uditivo': [
    'Facilitare la comunicazione con strategie adeguate',
    'Verificare funzionamento protesi acustica',
  ],
};

function ScaleCard({ title, items, values, onChange, maxScore, interpret, idPrefix = 'scale' }) {
  const total = Object.values(values).reduce((s, v) => s + (v || 0), 0);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600">{title}</p>
        <div className="flex items-center gap-2">
          <Badge className={`text-sm px-3 py-1 ${interpret(total).color}`}>{total}{maxScore ? `/${maxScore}` : ''}</Badge>
          <span className="text-xs text-slate-500">{interpret(total).label}</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 p-2 rounded bg-slate-50">
            <span className="text-xs font-medium text-slate-700 w-48 flex-shrink-0">{item.nome}</span>
            <div className="flex gap-1 flex-wrap flex-1">
              {item.opzioni.map((op) => (
                <button
                  key={op.v}
                  onClick={() => onChange(idx, op.v)}
                  className={`px-2 py-1 text-[10px] rounded-md border transition-all ${
                    values[idx] === op.v
                      ? 'bg-brand text-white border-brand shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                  data-testid={`${idPrefix}-${idx}-${op.v}`}
                >
                  {op.l} ({op.v})
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// === CDR rendering helpers ===
const CDR_LEVEL_SELECTED = {
  0: 'bg-emerald-600 border-emerald-600',
  0.5: 'bg-amber-500 border-amber-500',
  1: 'bg-orange-500 border-orange-500',
  2: 'bg-rose-500 border-rose-500',
  3: 'bg-red-600 border-red-600',
};
const CDR_LEVEL_CHIP = {
  0: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  0.5: 'bg-amber-50 text-amber-700 border-amber-200',
  1: 'bg-orange-50 text-orange-700 border-orange-200',
  2: 'bg-rose-50 text-rose-700 border-rose-200',
  3: 'bg-red-50 text-red-700 border-red-200',
};
const CDR_STAGE_LABEL = {
  0: 'Demenza assente', 0.5: 'Demenza molto lieve', 1: 'Demenza lieve',
  2: 'Demenza moderata', 3: 'Demenza grave', 4: 'Demenza molto grave', 5: 'Demenza terminale',
};
const CDR_STAGE_COLOR = {
  0: 'bg-emerald-100 text-emerald-700', 0.5: 'bg-amber-100 text-amber-700',
  1: 'bg-orange-100 text-orange-700', 2: 'bg-rose-100 text-rose-700',
  3: 'bg-red-100 text-red-700', 4: 'bg-red-200 text-red-800', 5: 'bg-slate-800 text-white',
};

function CDRScaleCard({ values, onChange, editing, stadioAvanzato, onStadioChange }) {
  const compilati = CDRS_ITEMS.filter((_, i) => values[i] !== undefined && values[i] !== null).length;
  const sob = Object.values(values).reduce((s, v) => s + (v || 0), 0);
  const calcolato = calcolaCDRGlobale(values);
  const globale = stadioAvanzato != null ? stadioAvanzato : calcolato;

  return (
    <div className="space-y-3" data-testid="cdrs-scale-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-slate-600">CDR - Clinical Dementia Rating Scale (Estesa)</p>
          <p className="text-[10px] text-slate-400">Morris J.C. (1997) - estensione stadi 4-5 Heyman et al. (1987)</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]" data-testid="cdrs-sob-badge">Somma celle {sob}/18</Badge>
          {globale != null ? (
            <Badge className={`text-sm px-3 py-1 ${CDR_STAGE_COLOR[globale]}`} data-testid="cdrs-globale-badge">
              CDR {globale} - {CDR_STAGE_LABEL[globale]}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] text-slate-400" data-testid="cdrs-globale-badge">
              CDR globale: valutare memoria + almeno 3 domini ({compilati}/6)
            </Badge>
          )}
        </div>
      </div>

      <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 leading-relaxed">
        <strong>N.B.</strong> Assegnare il punteggio solo se il deficit dipende da deterioramento cognitivo e non da altre cause.
        Sono necessarie informazioni raccolte da un familiare o operatore che conosce il soggetto. Ogni dominio va valutato in modo indipendente dagli altri.
      </p>

      {CDRS_ITEMS.map((item, idx) => (
        <div key={idx} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`cdrs-domain-${idx}`}>
          <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-slate-50 border-b border-slate-100">
            <span className="text-xs font-semibold text-slate-700">
              {item.nome}
              {item.primaria && <Badge className="ml-1.5 text-[8px] bg-blue-100 text-blue-700 px-1">categoria primaria</Badge>}
            </span>
            {values[idx] != null && (
              <Badge className={`text-[10px] border ${CDR_LEVEL_CHIP[values[idx]]}`}>CDR {values[idx]}</Badge>
            )}
          </div>
          {item.nota && <p className="px-2.5 pt-1.5 text-[9px] text-slate-400 italic">{item.nota}</p>}
          <div className="divide-y divide-slate-100">
            {item.opzioni.map((op) => {
              const sel = values[idx] === op.v;
              return (
                <button
                  key={op.v}
                  type="button"
                  disabled={!editing}
                  onClick={() => onChange(idx, op.v)}
                  className={`w-full flex items-start gap-2 text-left px-2.5 py-1.5 transition-colors disabled:cursor-default ${
                    sel ? 'bg-blue-50' : 'hover:bg-slate-50 disabled:hover:bg-transparent'
                  }`}
                  data-testid={`cdrs-item-${idx}-opt-${op.v}`}
                >
                  <span className={`mt-px shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold border text-white ${
                    sel ? CDR_LEVEL_SELECTED[op.v] : 'bg-slate-300 border-slate-300'
                  }`}>CDR {op.v}</span>
                  <span className={`text-[11px] leading-snug ${sel ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>{op.l}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="space-y-2">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Stadi Avanzati (sovrascrivono il CDR calcolato)</p>
        {CDR_STADI_AVANZATI.map(stadio => {
          const sel = stadioAvanzato === stadio.valore;
          return (
            <button
              key={stadio.valore}
              type="button"
              disabled={!editing}
              onClick={() => onStadioChange(stadio.valore)}
              className={`w-full text-left p-2.5 rounded-lg border transition-colors disabled:cursor-default ${
                sel ? 'bg-red-50 border-red-300 ring-1 ring-red-300' : 'border-slate-200 hover:bg-slate-50 disabled:hover:bg-transparent'
              }`}
              data-testid={`cdrs-stadio-${stadio.valore}`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${sel ? 'bg-red-600 border-red-600' : 'border-slate-300'}`}>
                  {sel && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                </span>
                <span className="text-xs font-semibold text-slate-700">{stadio.nome}</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-1 ml-6 leading-snug">{stadio.descrizione}</p>
            </button>
          );
        })}
      </div>

      <details className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
        <summary className="cursor-pointer font-semibold text-slate-600">Regole di calcolo del CDR globale</summary>
        <ul className="mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
          <li>La <strong>memoria</strong> e la categoria primaria; le altre cinque sono secondarie.</li>
          <li>Se almeno tre categorie secondarie hanno lo stesso punteggio della memoria, il CDR e uguale al punteggio della memoria.</li>
          <li>Se tre o piu categorie secondarie hanno un valore piu alto o piu basso della memoria, il CDR corrisponde al punteggio della maggioranza delle secondarie.</li>
          <li>Se due categorie hanno un valore superiore e due un valore inferiore rispetto alla memoria, il CDR corrisponde a quello della memoria.</li>
        </ul>
      </details>
    </div>
  );
}

function CheckboxGrid({ items, selected, onChange, columns = 2 }) {
  const toggle = (item) => {
    if (selected.includes(item)) onChange(selected.filter(i => i !== item));
    else onChange([...selected, item]);
  };
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-${columns} gap-1.5`}>
      {items.map((item) => (
        <label key={item} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-xs ${selected.includes(item) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-slate-200 hover:bg-slate-50 text-slate-600'}`}>
          <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(item)} className="rounded border-slate-300 text-brand w-3.5 h-3.5" />
          <span>{item}</span>
        </label>
      ))}
    </div>
  );
}

export default function PAITab({ ospiteId, ospite }) {
  const { hasRole } = useAuth();
  const [paiList, setPaiList] = useState([]);
  const [currentPai, setCurrentPai] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('patologie');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Form state
  const [patologieSel, setPatologieSel] = useState([]);
  const [intFarma, setIntFarma] = useState('');
  const [intAlim, setIntAlim] = useState('');
  const [bisogni, setBisogni] = useState([]);
  const [attInf, setAttInf] = useState([]);
  const [attFisio, setAttFisio] = useState([]);
  const [attOss, setAttOss] = useState([]);
  const [attTer, setAttTer] = useState([]);
  const [barthel, setBarthel] = useState({});
  const [braden, setBraden] = useState({});
  const [conley, setConley] = useState({});
  const [cdrs, setCdrs] = useState({});
  const [cdrsStadioAvanzato, setCdrsStadioAvanzato] = useState(null);
  const [lesioni, setLesioni] = useState([]);
  const [obiettivi, setObiettivi] = useState([]);
  const [interventi, setInterventi] = useState([]);
  const [freqRiv, setFreqRiv] = useState('trimestrale');
  const [dataRiv, setDataRiv] = useState('');
  const [note, setNote] = useState('');

  const fetchPai = useCallback(async () => {
    try {
      const res = await api.get(`/ospiti/${ospiteId}/pai`);
      setPaiList(res.data);
      if (res.data.length > 0) loadPai(res.data[0]);
    } catch (e) { console.error('Errore caricamento PAI:', e); }
    setLoading(false);
  }, [ospiteId]);

  useEffect(() => { fetchPai(); }, [fetchPai]);

  const loadPai = (p) => {
    setCurrentPai(p);
    setPatologieSel(p.patologie_prevalenti || []);
    setIntFarma(p.intolleranze_farmacologiche || '');
    setIntAlim(p.intolleranze_alimentari || '');
    setBisogni(p.bisogni_rilevati || []);
    setAttInf(p.attivita_infermieristiche || []);
    setAttFisio(p.attivita_fisioterapiche || []);
    setAttOss(p.attivita_oss || []);
    setAttTer(p.attivita_terapista || []);
    setLesioni(p.lesioni || []);
    setObiettivi(p.obiettivi || []);
    setInterventi(p.interventi || []);
    setFreqRiv(p.frequenza_rivalutazione || 'trimestrale');
    setDataRiv(p.data_prossima_rivalutazione || '');
    setNote(p.note || '');
    // Load scale values
    const bv = {}, brv = {}, cv = {}, cdv = {};
    (p.valutazioni || []).forEach(v => {
      if (v.scala === 'barthel' && v.dettaglio) Object.entries(v.dettaglio).forEach(([k, val]) => bv[parseInt(k)] = val);
      if (v.scala === 'braden' && v.dettaglio) Object.entries(v.dettaglio).forEach(([k, val]) => brv[parseInt(k)] = val);
      if (v.scala === 'conley' && v.dettaglio) Object.entries(v.dettaglio).forEach(([k, val]) => cv[parseInt(k)] = val);
      if (v.scala === 'cdrs' && v.dettaglio) Object.entries(v.dettaglio).forEach(([k, val]) => cdv[parseInt(k)] = val);
    });
    setBarthel(bv);
    setBraden(brv);
    setConley(cv);
    setCdrs(cdv);
    setCdrsStadioAvanzato(p.cdrs_stadio_avanzato || null);
    setEditing(false);
  };

  const calcDataRiv = (freq) => {
    const d = new Date();
    if (freq === 'mensile') d.setMonth(d.getMonth() + 1);
    else if (freq === 'trimestrale') d.setMonth(d.getMonth() + 3);
    else if (freq === 'semestrale') d.setMonth(d.getMonth() + 6);
    else if (freq === 'annuale') d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  };

  const handleFreqChange = (v) => {
    setFreqRiv(v);
    setDataRiv(calcDataRiv(v));
  };

  const buildPayload = () => {
    const barthelTotal = Object.values(barthel).reduce((s, v) => s + (v || 0), 0);
    const bradenTotal = Object.values(braden).reduce((s, v) => s + (v || 0), 0);
    const conleyTotal = Object.values(conley).reduce((s, v) => s + (v || 0), 0);
    const cdrsTotal = Object.values(cdrs).reduce((s, v) => s + (v || 0), 0);
    const cdrsGlobale = calcolaCDRGlobale(cdrs);
    const valutazioni = [
      { scala: 'barthel', punteggio: barthelTotal, data: new Date().toISOString().slice(0, 10), dettaglio: barthel },
      { scala: 'braden', punteggio: bradenTotal, data: new Date().toISOString().slice(0, 10), dettaglio: braden },
      { scala: 'conley', punteggio: conleyTotal, data: new Date().toISOString().slice(0, 10), dettaglio: conley },
      { scala: 'cdrs', punteggio: cdrsTotal, cdr_globale: cdrsStadioAvanzato || cdrsGlobale, data: new Date().toISOString().slice(0, 10), dettaglio: cdrs },
    ];
    return {
      patologie_prevalenti: patologieSel,
      intolleranze_farmacologiche: intFarma,
      intolleranze_alimentari: intAlim,
      bisogni_rilevati: bisogni,
      attivita_infermieristiche: attInf,
      attivita_fisioterapiche: attFisio,
      attivita_oss: attOss,
      attivita_terapista: attTer,
      valutazioni,
      lesioni,
      obiettivi,
      interventi,
      frequenza_rivalutazione: freqRiv,
      data_prossima_rivalutazione: dataRiv,
      note,
      cdrs_stadio_avanzato: cdrsStadioAvanzato,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (currentPai) {
        await api.put(`/ospiti/${ospiteId}/pai/${currentPai.id}`, buildPayload());
        toast.success('PAI aggiornato');
      } else {
        await api.post(`/ospiti/${ospiteId}/pai`, buildPayload());
        toast.success('PAI creato');
      }
      fetchPai();
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!currentPai) return;
    try {
      await api.delete(`/ospiti/${ospiteId}/pai/${currentPai.id}`);
      toast.success('PAI eliminato');
      setCurrentPai(null);
      setEditing(false);
      const res = await api.get(`/ospiti/${ospiteId}/pai`);
      setPaiList(res.data);
      if (res.data.length > 0) loadPai(res.data[0]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore eliminazione'); }
    setConfirmDelete(false);
  };

  const startNew = () => {    setCurrentPai(null);
    setPatologieSel([]); setIntFarma(''); setIntAlim('');
    // Auto-populate intolleranze from ospite allergie
    if (ospite?.allergie?.length > 0) {
      const farmaAllergie = ospite.allergie.map(a => `${a.sostanza} (${a.reazione} - ${a.gravita})`).join('; ');
      setIntFarma(farmaAllergie);
    }
    setBisogni([]); setAttInf([]); setAttFisio([]); setAttOss([]); setAttTer([]);
    setBarthel({}); setBraden({}); setConley({}); setCdrs({}); setCdrsStadioAvanzato(null);
    setLesioni([]); setObiettivi([]); setInterventi([]);
    setNote(''); setDataRiv(calcDataRiv('trimestrale'));
    setEditing(true);
  };

  const interpretBarthel = (v) => {
    if (v >= 80) return { label: 'Lieve dipendenza', color: 'bg-emerald-100 text-emerald-700' };
    if (v >= 60) return { label: 'Moderata dipendenza', color: 'bg-amber-100 text-amber-700' };
    if (v >= 40) return { label: 'Grave dipendenza', color: 'bg-orange-100 text-orange-700' };
    return { label: 'Totale dipendenza', color: 'bg-red-100 text-red-700' };
  };
  const interpretBraden = (v) => {
    if (v >= 19) return { label: 'Rischio basso', color: 'bg-emerald-100 text-emerald-700' };
    if (v >= 15) return { label: 'Rischio moderato', color: 'bg-amber-100 text-amber-700' };
    if (v >= 13) return { label: 'Rischio alto', color: 'bg-orange-100 text-orange-700' };
    return { label: 'Rischio molto alto', color: 'bg-red-100 text-red-700' };
  };
  const interpretConley = (v) => {
    if (v <= 1) return { label: 'Rischio basso', color: 'bg-emerald-100 text-emerald-700' };
    if (v <= 3) return { label: 'Rischio moderato', color: 'bg-amber-100 text-amber-700' };
    return { label: 'Rischio alto', color: 'bg-red-100 text-red-700' };
  };

  if (loading) return <p className="text-center py-8 text-slate-400">Caricamento PAI...</p>;

  return (
    <div className="space-y-4" data-testid="pai-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold text-slate-800">Piano Assistenziale Individualizzato</h3>
          {currentPai && <Badge variant="outline" className="text-[10px]">{currentPai.stato || 'attivo'} - {new Date(currentPai.created_at).toLocaleDateString('it-IT')}</Badge>}
        </div>
        <div className="flex gap-2">
          {!editing && hasRole('coordinatore', 'medico', 'infermiere') && (
            <>
              {currentPai && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-pai-btn"><ClipboardList className="w-3.5 h-3.5 mr-1" />Modifica</Button>}
              {currentPai && hasRole('coordinatore', 'medico') && (
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} className="text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200" data-testid="delete-pai-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" />Elimina
                </Button>
              )}
              <Button size="sm" onClick={startNew} className="bg-brand hover:bg-brand-dark text-white" data-testid="new-pai-btn"><Plus className="w-3.5 h-3.5 mr-1" />Nuovo PAI</Button>
            </>
          )}
          {editing && (
            <>
              <Button variant="outline" size="sm" onClick={() => { if (currentPai) loadPai(currentPai); else setEditing(false); }}>Annulla</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-dark text-white" data-testid="save-pai-btn">
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Salvataggio...' : 'Salva PAI'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* MMG Section */}
      <MMGSection ospite={ospite} ospiteId={ospiteId} />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Elimina PAI"
        message={currentPai ? `Eliminare definitivamente il PAI del ${new Date(currentPai.created_at).toLocaleDateString('it-IT')}? Questa azione non puo essere annullata.` : ''}
      />

      {/* PAI Content */}
      {!currentPai && !editing ? (
        <Card><CardContent className="py-12 text-center text-slate-400"><ClipboardList className="w-10 h-10 mx-auto mb-3" /><p className="text-sm">Nessun PAI presente. Crea il primo piano assistenziale.</p></CardContent></Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
            <TabsTrigger value="patologie" className="text-[11px]"><Heart className="w-3 h-3 mr-1" />Patologie</TabsTrigger>
            <TabsTrigger value="bisogni" className="text-[11px]"><AlertCircle className="w-3 h-3 mr-1" />Bisogni</TabsTrigger>
            <TabsTrigger value="scale" className="text-[11px]"><Activity className="w-3 h-3 mr-1" />Scale Valutazione</TabsTrigger>
            <TabsTrigger value="lesioni" className="text-[11px]"><MapPin className="w-3 h-3 mr-1" />Lesioni</TabsTrigger>
            <TabsTrigger value="attivita" className="text-[11px]"><Users className="w-3 h-3 mr-1" />Attivita</TabsTrigger>
            <TabsTrigger value="obiettivi" className="text-[11px]"><CheckCircle className="w-3 h-3 mr-1" />Obiettivi</TabsTrigger>
          </TabsList>

          {/* PATOLOGIE */}
          <TabsContent value="patologie" className="mt-4 space-y-4">
            <Card><CardContent className="pt-4 space-y-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Patologie Prevalenti</p>
              <CheckboxGrid items={PATOLOGIE} selected={patologieSel.map(p => typeof p === 'string' ? p : p.nome || p)} onChange={v => { if (editing) setPatologieSel(v); }} columns={2} />
              <Separator />
              {ospite?.allergie?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1.5">Allergie Registrate in Anagrafica</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {ospite.allergie.map((a, i) => (
                      <Badge key={i} variant="outline" className={`text-[10px] ${a.gravita === 'grave' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-orange-100 text-orange-700 border-orange-300'}`}>{a.sostanza} - {a.reazione}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label className="text-xs">Intolleranze Farmacologiche</Label>
                  <DictableTextarea value={intFarma} onChange={e => editing && setIntFarma(e.target.value)} rows={2} disabled={!editing} placeholder="es. Penicillina, FANS..." /></div>
                <div className="space-y-1"><Label className="text-xs">Intolleranze Alimentari</Label>
                  <DictableTextarea value={intAlim} onChange={e => editing && setIntAlim(e.target.value)} rows={2} disabled={!editing} placeholder="es. Glutine, Lattosio..." /></div>
              </div>
            </CardContent></Card>
          </TabsContent>

          {/* BISOGNI */}
          <TabsContent value="bisogni" className="mt-4">
            <Card><CardContent className="pt-4 space-y-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Bisogni Assistenziali Rilevati</p>
              <CheckboxGrid items={BISOGNI} selected={bisogni} onChange={v => { if (editing) setBisogni(v); }} columns={2} />
            </CardContent></Card>
          </TabsContent>

          {/* SCALE VALUTAZIONE */}
          <TabsContent value="scale" className="mt-4 space-y-4">
            <Card><CardContent className="pt-4">
              <ScaleCard title="Scala di Barthel - Indice di Indipendenza ADL" items={BARTHEL_ITEMS}
                values={barthel} onChange={(i, v) => editing && setBarthel(prev => ({ ...prev, [i]: v }))}
                maxScore={100} interpret={interpretBarthel} idPrefix="barthel" />
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <ScaleCard title="Scala di Braden - Rischio Lesioni da Pressione" items={BRADEN_ITEMS}
                values={braden} onChange={(i, v) => editing && setBraden(prev => ({ ...prev, [i]: v }))}
                maxScore={23} interpret={interpretBraden} idPrefix="braden" />
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <ScaleCard title="Scala di Conley - Rischio Caduta" items={CONLEY_ITEMS}
                values={conley} onChange={(i, v) => editing && setConley(prev => ({ ...prev, [i]: v }))}
                maxScore={10} interpret={interpretConley} idPrefix="conley" />
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <CDRScaleCard
                values={cdrs}
                editing={editing}
                onChange={(i, v) => editing && setCdrs(prev => ({ ...prev, [i]: v }))}
                stadioAvanzato={cdrsStadioAvanzato}
                onStadioChange={(val) => editing && setCdrsStadioAvanzato(prev => prev === val ? null : val)}
              />
            </CardContent></Card>
          </TabsContent>

          {/* LESIONI */}
          <TabsContent value="lesioni" className="mt-4">
            <Card><CardContent className="pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Lesioni Cutanee / Lesioni da Pressione</p>
                {editing && <Button size="sm" variant="outline" onClick={() => setLesioni(l => [...l, { sede: '', lato: '', tipo: 'pressione', stadio: 'I', note: '', protocollo_medicazione: '' }])} data-testid="add-lesione-btn"><Plus className="w-3 h-3 mr-1" />Aggiungi Lesione</Button>}
              </div>
              <BodyMapSelector lesioni={lesioni} onChange={editing ? setLesioni : undefined} />
              {lesioni.map((les, i) => (
                <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2" data-testid={`lesione-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600">Lesione {i + 1}: {les.sede || 'Non localizzata'}</span>
                    {editing && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setLesioni(l => l.filter((_, idx) => idx !== i))}><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="space-y-0.5"><Label className="text-[10px]">Sede</Label><Input value={les.sede} onChange={e => { const n = [...lesioni]; n[i].sede = e.target.value; setLesioni(n); }} disabled={!editing} className="h-7 text-xs" /></div>
                    <div className="space-y-0.5"><Label className="text-[10px]">Lato</Label>
                      <Select value={les.lato} onValueChange={v => { const n = [...lesioni]; n[i].lato = v; setLesioni(n); }} disabled={!editing}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="dx">Destro</SelectItem><SelectItem value="sx">Sinistro</SelectItem><SelectItem value="bilaterale">Bilaterale</SelectItem></SelectContent>
                      </Select></div>
                    <div className="space-y-0.5"><Label className="text-[10px]">Tipo</Label>
                      <Select value={les.tipo} onValueChange={v => { const n = [...lesioni]; n[i].tipo = v; setLesioni(n); }} disabled={!editing}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="pressione">Da pressione</SelectItem><SelectItem value="vascolare">Vascolare</SelectItem><SelectItem value="diabetica">Diabetica</SelectItem><SelectItem value="traumatica">Traumatica</SelectItem><SelectItem value="chirurgica">Chirurgica</SelectItem></SelectContent>
                      </Select></div>
                    <div className="space-y-0.5"><Label className="text-[10px]">Stadio</Label>
                      <Select value={les.stadio} onValueChange={v => { const n = [...lesioni]; n[i].stadio = v; setLesioni(n); }} disabled={!editing}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="I">Stadio I</SelectItem><SelectItem value="II">Stadio II</SelectItem><SelectItem value="III">Stadio III</SelectItem><SelectItem value="IV">Stadio IV</SelectItem></SelectContent>
                      </Select></div>
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-slate-500">Protocollo Medicazione / Note</Label>
                    <Textarea
                      value={les.protocollo_medicazione || ''}
                      onChange={e => { const n = [...lesioni]; n[i].protocollo_medicazione = e.target.value; setLesioni(n); }}
                      disabled={!editing}
                      rows={2}
                      className="text-xs resize-none"
                      placeholder="Descrivere il protocollo di medicazione, frequenza, prodotti utilizzati..."
                      data-testid={`lesione-${i}-protocollo`}
                    />
                  </div>
                </div>
              ))}
            </CardContent></Card>
          </TabsContent>

          {/* ATTIVITA */}
          <TabsContent value="attivita" className="mt-4 space-y-4">
            <Card><CardContent className="pt-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Attivita Infermieristiche</p>
              <CheckboxGrid items={ATTIVITA_INFERMIERISTICHE} selected={attInf} onChange={v => { if (editing) setAttInf(v); }} columns={2} />
            </CardContent></Card>
            <Card><CardContent className="pt-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Attivita Fisioterapiche</p>
              <CheckboxGrid items={ATTIVITA_FISIO} selected={attFisio} onChange={v => { if (editing) setAttFisio(v); }} columns={2} />
            </CardContent></Card>
            <Card><CardContent className="pt-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Attivita OSS</p>
              <CheckboxGrid items={ATTIVITA_OSS} selected={attOss} onChange={v => { if (editing) setAttOss(v); }} columns={2} />
            </CardContent></Card>
            <Card><CardContent className="pt-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Attivita Terapista Occupazionale</p>
              <CheckboxGrid items={ATTIVITA_TERAPISTA} selected={attTer} onChange={v => { if (editing) setAttTer(v); }} columns={2} />
            </CardContent></Card>
          </TabsContent>

          {/* OBIETTIVI */}
          <TabsContent value="obiettivi" className="mt-4 space-y-4">
            {/* Dynamic objectives from Bisogni */}
            {bisogni.length > 0 && (
              <Card><CardContent className="pt-4 space-y-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Obiettivi Suggeriti dai Bisogni Rilevati</p>
                <div className="space-y-3">
                  {bisogni.map(bisogno => {
                    const suggeriti = OBIETTIVI_PER_BISOGNO[bisogno];
                    if (!suggeriti || suggeriti.length === 0) return null;
                    return (
                      <div key={bisogno} className="p-3 rounded-lg border border-blue-100 bg-blue-50/40 space-y-1.5">
                        <p className="text-[11px] font-semibold text-blue-700">{bisogno}</p>
                        {suggeriti.map((sug, si) => {
                          const alreadyAdded = obiettivi.some(o => o.descrizione === sug);
                          return (
                            <div key={si} className="flex items-center gap-2">
                              <span className="text-xs text-slate-600 flex-1">{sug}</span>
                              {editing && !alreadyAdded && (
                                <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                                  onClick={() => setObiettivi(o => [...o, { descrizione: sug, stato: 'attivo', scadenza: '', bisogno_correlato: bisogno }])}
                                  data-testid={`add-suggested-obj-${si}`}
                                ><Plus className="w-3 h-3 mr-0.5" />Aggiungi</Button>
                              )}
                              {alreadyAdded && <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Aggiunto</Badge>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </CardContent></Card>
            )}

            <Card><CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Obiettivi Assistenziali</p>
                {editing && <Button size="sm" variant="outline" onClick={() => setObiettivi(o => [...o, { descrizione: '', stato: 'attivo', scadenza: '' }])} data-testid="add-obiettivo-btn"><Plus className="w-3 h-3 mr-1" />Aggiungi</Button>}
              </div>
              {obiettivi.length === 0 && <p className="text-xs text-slate-400 py-2">Nessun obiettivo. {bisogni.length > 0 ? 'Usa i suggerimenti sopra o aggiungine uno manualmente.' : 'Seleziona dei bisogni nella tab apposita per ricevere suggerimenti.'}</p>}
              {obiettivi.map((ob, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg border" data-testid={`obiettivo-${i}`}>
                  <Input value={ob.descrizione} onChange={e => { const n = [...obiettivi]; n[i].descrizione = e.target.value; setObiettivi(n); }} disabled={!editing} className="flex-1 text-xs h-8" placeholder="Descrizione obiettivo" />
                  {ob.bisogno_correlato && <Badge variant="outline" className="text-[9px] shrink-0">{ob.bisogno_correlato}</Badge>}
                  <DatePickerField value={ob.scadenza} onChange={v => { const n = [...obiettivi]; n[i].scadenza = v; setObiettivi(n); }} disabled={!editing} className="w-36 text-xs h-8" />
                  {editing && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setObiettivi(o => o.filter((_, idx) => idx !== i))}><Trash2 className="w-3 h-3" /></Button>}
                </div>
              ))}
            </CardContent></Card>
            <Card><CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Interventi Programmati</p>
                {editing && <Button size="sm" variant="outline" onClick={() => setInterventi(iv => [...iv, { descrizione: '', frequenza: 'giornaliera', responsabile: '' }])}><Plus className="w-3 h-3 mr-1" />Aggiungi</Button>}
              </div>
              {interventi.map((iv, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg border">
                  <Input value={iv.descrizione} onChange={e => { const n = [...interventi]; n[i].descrizione = e.target.value; setInterventi(n); }} disabled={!editing} className="flex-1 text-xs h-8" placeholder="Descrizione intervento" />
                  <Select value={iv.frequenza} onValueChange={v => { const n = [...interventi]; n[i].frequenza = v; setInterventi(n); }} disabled={!editing}>
                    <SelectTrigger className="w-32 text-xs h-8"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="giornaliera">Giornaliera</SelectItem><SelectItem value="bisettimanale">Bisettimanale</SelectItem><SelectItem value="settimanale">Settimanale</SelectItem><SelectItem value="mensile">Mensile</SelectItem><SelectItem value="al bisogno">Al bisogno</SelectItem></SelectContent>
                  </Select>
                  <Input value={iv.responsabile} onChange={e => { const n = [...interventi]; n[i].responsabile = e.target.value; setInterventi(n); }} disabled={!editing} className="w-28 text-xs h-8" placeholder="Ruolo" />
                  {editing && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setInterventi(iv => iv.filter((_, idx) => idx !== i))}><Trash2 className="w-3 h-3" /></Button>}
                </div>
              ))}
            </CardContent></Card>
            <Card><CardContent className="pt-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Rivalutazione</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">Frequenza</Label>
                  <Select value={freqRiv} onValueChange={v => editing && handleFreqChange(v)} disabled={!editing}>
                    <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="mensile">Mensile</SelectItem><SelectItem value="trimestrale">Trimestrale</SelectItem><SelectItem value="semestrale">Semestrale</SelectItem><SelectItem value="annuale">Annuale</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Prossima Rivalutazione</Label>
                  <DatePickerField value={dataRiv} onChange={v => editing && setDataRiv(v)} disabled={!editing} className={dataRiv && new Date(dataRiv) <= new Date() ? 'border-red-300 bg-red-50/50' : ''} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Note</Label>
                <DictableTextarea value={note} onChange={e => editing && setNote(e.target.value)} rows={3} disabled={!editing} placeholder="Note aggiuntive..." /></div>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      )}

      {/* PAI History */}
      {paiList.length > 1 && (
        <Card><CardContent className="pt-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Storico PAI</p>
          <div className="flex gap-2 flex-wrap">
            {paiList.map((p, i) => (
              <Button key={p.id} variant={currentPai?.id === p.id ? 'default' : 'outline'} size="sm" className="text-xs" onClick={() => loadPai(p)}>
                PAI {new Date(p.created_at).toLocaleDateString('it-IT')}
                {i === 0 && <Badge className="ml-1 text-[9px]">Attuale</Badge>}
              </Button>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
